/**
 * SalesOrderEditPage (S2.3) — the SEPARATE modify flow (SO-PO board:
 * 一條建立、一條修改), reusing the S2.2 create surface's building blocks
 * (SoLineEditor incl. add-line, DraftSoLine derivations, the resolve flow,
 * form card layout) against PATCH /sales-orders/:id.
 *
 * - Draft/confirmed ONLY — later statuses render a locked notice (the
 *   handler enforces SO_LOCKED 409 as the backstop).
 * - Customer and external order number are FIXED on modify (the PATCH
 *   contract carries neither) — rendered as read-only meta, not disabled
 *   inputs.
 * - Modify REPLACES lines: every saved line gets a NEW price snapshot
 *   (plan §S2.1). On load, lines re-resolve against the live resolver so
 *   what is previewed equals what the save persists; manual overrides
 *   (unitPrice + logged reason) survive re-resolution untouched.
 * - Any prior 價格審核 release is void after a line replacement — the
 *   handler re-derives priceStatus and clears priceApproval.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Alert, Button, Col, DatePicker, Row, Select, Typography, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import { extractApiError, useReadyProducts, useSalesOrder } from '@/hooks/useSalesOrders';
import { useFreshMinimumMarginPct } from '@/hooks/usePricing';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { salesOrderApi } from '@/services/api';
import type {
  SalesOrderLine,
  SalesOrderPriceHoldReason,
  UpdateSalesOrderRequest,
} from '@/types/api/salesOrder';
import {
  SALES_ORDER_PRICE_HOLD_REASONS,
  SO_PRICE_DEVIATION_THRESHOLD_PCT,
} from '@/types/api/salesOrder';
import type { Product } from '@/types/api/product';
import SoLineEditor from './components/SoLineEditor';
import {
  draftLineHoldReasons,
  draftLineLandedCost,
  draftLineMargin,
  draftLineTotal,
  newDraftSoLine,
} from './components/soDraftLine';
import type { DraftSoLine } from './components/soDraftLine';
import { formatMoney, formatPct, holdReasonKey } from './components/soFormat';
import {
  appendSoLineOverrideAudit,
  appendSoLineReResolveAudit,
} from './components/soPriceOverrideAudit';
import { roundHalfUp2 } from '@/lib/pricing';
import { useSalesOrderDetailActions } from './SalesOrderDetailPage/useSalesOrderDetailActions';

const { Text } = Typography;

/** Mock-narrative currencies (FX AUD/USD/TWD) — parity with the create page. */
const CURRENCY_OPTIONS = ['AUD', 'USD', 'TWD'];

const editSoSchema = z
  .object({
    currency: z.string().min(1),
    expectedShipDate: z.string().min(1, 'orders:so.validation.expectedShipDateRequired'),
    deliveryDate: z.string().min(1, 'orders:so.validation.deliveryDateRequired'),
  })
  .refine(
    (data) =>
      !data.expectedShipDate || !data.deliveryDate || data.deliveryDate >= data.expectedShipDate,
    { path: ['deliveryDate'], message: 'orders:so.validation.deliveryBeforeShip' }
  );

type EditSoFormData = z.infer<typeof editSoSchema>;

const defaultFormValues: EditSoFormData = {
  currency: 'AUD',
  expectedShipDate: '',
  deliveryDate: '',
};

/** Persisted line → editable draft (resolution starts from the old snapshot). */
function toDraftLine(line: SalesOrderLine): DraftSoLine {
  return {
    key: line.id,
    productId: line.productId,
    sku: line.sku,
    productName: line.productName,
    customerPartNumber: line.customerPartNumber ?? null,
    qty: line.qty,
    resolving: false,
    resolution: line.priceTrace,
    unitPrice: line.unitPrice,
    overridden: Boolean(line.overrideReason),
    overrideReason: line.overrideReason ?? null,
    // RULING-7 queue is a CREATE-page mechanism — the modify flow writes its
    // detach row immediately against the persisted SO; never queued here.
    detachedOverride: null,
  };
}

const SalesOrderEditPage = () => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const navigate = useNavigate();
  const { soId } = useParams<{ soId: string }>();

  const { salesOrder: so, isLoading } = useSalesOrder(soId);
  const { products, isLoading: productsLoading } = useReadyProducts();
  // Minimum margin (2026-09-15): the floor the handler will test on save.
  const minimumMarginPct = useFreshMinimumMarginPct();
  const { updateSalesOrder } = useSalesOrderDetailActions();

  const locked = so !== undefined && so.status !== 'draft' && so.status !== 'confirmed';

  const {
    control,
    handleSubmit,
    trigger,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EditSoFormData>({
    resolver: zodResolver(editSoSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const currency = watch('currency');

  const [lines, setLines] = useState<DraftSoLine[]>([]);
  const [linesError, setLinesError] = useState<string | null>(null);
  /** Persisted per-line discounts carried through the replace round-trip. */
  const discountByKeyRef = useRef<Map<string, number | undefined>>(new Map());

  // -------------------------------------------------------------------------
  // Line pricing — same resolve flow as the create page, with one modify-flow
  // difference: an existing manual override (price + logged reason) survives
  // re-resolution; only non-overridden lines take the fresh resolved price.
  // -------------------------------------------------------------------------

  // The optional `qty` reaches the transport ONLY from the explicit S3.3
  // seat (`handleReResolveAtQty`, keepOverride:false after its confirm) —
  // hydration/product-select callers keep the 4-arg shape and never pass it.
  const resolveLine = useCallback(
    async (
      key: string,
      productId: string,
      forCustomerId: number | null,
      keepOverride: boolean,
      qty?: number
    ) => {
      setLines((prev) =>
        prev.map((line) => (line.key === key ? { ...line, resolving: true } : line))
      );
      try {
        const { data } = await salesOrderApi.resolveLinePrice({
          productId,
          customerId: forCustomerId,
          ...(qty != null ? { qty } : {}),
        });
        setLines((prev) =>
          prev.map((line) =>
            line.key === key && line.productId === productId
              ? {
                  ...line,
                  resolving: false,
                  resolution: data,
                  ...(keepOverride && line.overridden
                    ? {}
                    : {
                        unitPrice: data.unitPrice,
                        overridden: false,
                        overrideReason: null,
                      }),
                }
              : line
          )
        );
        return data;
      } catch {
        setLines((prev) =>
          prev.map((line) =>
            line.key === key && !(keepOverride && line.overridden)
              ? { ...line, resolving: false, resolution: null, unitPrice: null }
              : line.key === key
                ? { ...line, resolving: false }
                : line
          )
        );
        message.error(t('orders:so.create.resolveFailed'));
        return null;
      }
    },
    [message, t]
  );

  // -------------------------------------------------------------------------
  // Hydration — once per loaded SO: form values + draft lines, then a fresh
  // resolve pass (modify authors NEW snapshots; preview must match persist).
  // -------------------------------------------------------------------------

  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!so || locked || hydratedForRef.current === so.id) return;
    hydratedForRef.current = so.id;
    reset({
      currency: so.currency,
      expectedShipDate: so.expectedShipDate ?? '',
      deliveryDate: so.deliveryDate ?? '',
    });
    discountByKeyRef.current = new Map(so.lines.map((line) => [line.id, line.discountPct]));
    setLines(so.lines.map(toDraftLine));
    for (const line of so.lines) {
      void resolveLine(line.id, line.productId, so.customerId, true);
    }
  }, [so, locked, reset, resolveLine]);

  const handleSelectProduct = (key: string, product: Product) => {
    const pn =
      so != null
        ? product.customerPartNumbers?.find((row) => row.customerId === so.customerId)
        : undefined;
    setLines((prev) =>
      prev.map((line) =>
        line.key === key
          ? {
              ...line,
              productId: product.id,
              sku: product.sku,
              productName: product.displayName || product.name,
              customerPartNumber: pn?.partNumber ?? null,
              resolution: null,
              unitPrice: null,
              overridden: false,
              overrideReason: null,
            }
          : line
      )
    );
    setLinesError(null);
    void resolveLine(key, product.id, so?.customerId ?? null, false);
  };

  // S3.3 (DA-6) — the ONLY qty-bearing resolve call: explicit, per-line,
  // user-triggered (the editor confirm-gates overridden lines first, per the
  // §6.2 detach consequence); keepOverride:false so the confirmed detach
  // actually replaces the override. A replaced override lands the standard
  // so_line detach audit row.
  const handleReResolveAtQty = async (key: string) => {
    if (!so) return;
    const line = lines.find((row) => row.key === key);
    if (!line?.productId || line.resolving) return;
    const detached =
      line.overridden && line.unitPrice !== null
        ? { unitPrice: line.unitPrice, reason: line.overrideReason ?? '' }
        : null;
    const resolution = await resolveLine(key, line.productId, so.customerId, false, line.qty);
    if (detached && resolution) {
      appendSoLineReResolveAudit({
        soRef: so.soNumber,
        sku: line.sku,
        currency,
        qty: line.qty,
        overriddenUnitPrice: detached.unitPrice,
        overrideReason: detached.reason,
        resolvedUnitPrice: resolution.unitPrice,
      });
    }
  };

  // -------------------------------------------------------------------------
  // Derived header totals (per-line + header margin — Board D profit)
  // -------------------------------------------------------------------------

  const totals = useMemo(() => {
    let amount = 0;
    let margin: number | null = null;
    // Lines whose cost is known but is in another currency: their margin is
    // not a number, so the header total must not pretend to include them.
    let marginBlind = 0;
    const holdReasons = new Set<SalesOrderPriceHoldReason>();
    for (const line of lines) {
      const lineTotal = draftLineTotal(line);
      if (lineTotal !== null) amount += lineTotal;
      const lineMargin = draftLineMargin(line, currency);
      if (lineMargin !== null) margin = (margin ?? 0) + roundHalfUp2(lineMargin.amount * line.qty);
      else if (draftLineLandedCost(line) !== null && line.unitPrice !== null) marginBlind += 1;
      for (const reason of draftLineHoldReasons(line, minimumMarginPct, currency)) {
        holdReasons.add(reason);
      }
    }
    return {
      amount: roundHalfUp2(amount),
      margin: margin !== null ? roundHalfUp2(margin) : null,
      marginPct: margin !== null && amount > 0 ? roundHalfUp2((margin / amount) * 100) : null,
      marginBlind,
      pendingApproval: holdReasons.size > 0,
      // Pinned order (deviation → low_margin) so the banner never reorders.
      holdReasons: SALES_ORDER_PRICE_HOLD_REASONS.filter((reason) => holdReasons.has(reason)),
    };
  }, [lines, minimumMarginPct, currency]);

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------

  const submit = async (data: EditSoFormData) => {
    if (!so) return;
    const activeLines = lines.filter((line) => line.productId);
    if (activeLines.length === 0) {
      setLinesError(t('orders:so.validation.linesRequired'));
      return;
    }
    if (activeLines.some((line) => line.resolving)) {
      setLinesError(t('orders:so.validation.linesResolving'));
      return;
    }
    const unpriced = activeLines.find((line) => line.unitPrice === null);
    if (unpriced) {
      setLinesError(t('orders:so.validation.linesUnpriced', { sku: unpriced.sku }));
      return;
    }
    setLinesError(null);

    const body: UpdateSalesOrderRequest = {
      currency: data.currency,
      expectedShipDate: data.expectedShipDate,
      deliveryDate: data.deliveryDate,
      lines: activeLines.map((line) => ({
        productId: line.productId!,
        qty: line.qty,
        customerPartNumber: line.customerPartNumber ?? undefined,
        discountPct: discountByKeyRef.current.get(line.key),
        // Overridden lines persist the manual price + logged reason; all
        // others omit unitPrice so the handler prices the NEW snapshot.
        ...(line.overridden && line.unitPrice !== null
          ? { unitPrice: line.unitPrice, overrideReason: line.overrideReason ?? undefined }
          : {}),
      })),
    };

    try {
      const updated = await updateSalesOrder(so.id, body);
      // Modify REPLACES lines — every persisted sales override on the saved
      // set lands a fresh 價格覆寫報表 review row (S9.2 governance).
      for (const line of activeLines) {
        if (line.overridden && line.unitPrice !== null && line.overrideReason) {
          appendSoLineOverrideAudit({
            soNumber: updated.soNumber,
            sku: line.sku,
            currency: data.currency,
            resolvedUnitPrice: line.resolution?.unitPrice ?? null,
            unitPrice: line.unitPrice,
            reason: line.overrideReason,
          });
        }
      }
      message.success(t('orders:so.edit.success', { soNumber: updated.soNumber }));
      if (updated.priceStatus === 'pending_approval') {
        message.info(t('orders:so.create.pendingApprovalInfo'));
      }
      navigate(`/auth/sales-orders/${so.id}`);
    } catch (error) {
      const { error: code, message: detail } = extractApiError(error);
      const known = ['OVERRIDE_REASON_REQUIRED', 'PRODUCT_NOT_READY', 'PRODUCT_INACTIVE', 'SO_LOCKED'];
      message.error(
        code && known.includes(code)
          ? t(`orders:so.errors.${code}`)
          : detail || t('common:messages.updateFailed')
      );
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const sectionCardStyle = {
    ...elevation.card,
    backgroundColor: token.colorBgContainer,
    marginBottom: token.marginLG,
  };
  const sectionHeaderStyle = {
    padding: '18px 24px',
    backgroundColor: token.colorFillQuaternary,
    borderRadius: `${radiusTokens.card}px ${radiusTokens.card}px 0 0`,
  };

  const fieldError = (key: keyof EditSoFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  const renderDateField = (name: 'expectedShipDate' | 'deliveryDate', label: string) => (
    <>
      <Text style={formStyles.labelStyle}>
        <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
        {label}
      </Text>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DatePicker
            value={field.value ? dayjs(field.value) : null}
            onChange={(value) => {
              field.onChange(value ? value.format('YYYY-MM-DD') : '');
              trigger(name);
            }}
            onBlur={field.onBlur}
            size="large"
            style={formStyles.inputStyle}
            status={errors[name] ? 'error' : undefined}
          />
        )}
      />
      {fieldError(name)}
    </>
  );

  const metaItem = (label: string, value: React.ReactNode) => (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {label}
      </Text>
      <Text style={{ fontWeight: token.fontWeightStrong }}>{value}</Text>
    </div>
  );

  // Locked statuses: the modify flow is closed — explain and route back.
  if (locked && so) {
    return (
      <div className="tw-px-6 tw-pb-8">
        <PageHeader title={t('orders:so.edit.title')} />
        <Alert
          type="warning"
          showIcon
          message={t('orders:so.edit.lockedTitle')}
          description={t('orders:so.edit.lockedDescription', {
            status: t(`orders:so.status.${so.status}`),
          })}
          action={
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/auth/sales-orders/${so.id}`)}
              style={{
                height: `${controlHeightTokens.controlHeightXL}px`,
                borderRadius: radiusTokens.card,
              }}
            >
              {t('orders:so.edit.backToDetail')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader
        title={so ? `${t('orders:so.edit.title')} ${so.soNumber}` : t('orders:so.edit.title')}
      />

      {/* 訂單資訊 Order info — customer/external # fixed, dates/currency editable */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('orders:so.create.orderInfo')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div className="tw-flex tw-gap-8 tw-flex-wrap">
                {metaItem(t('orders:fields.customer'), so?.customerName ?? '-')}
                {metaItem(
                  t('orders:so.fields.externalOrderNumber'),
                  so?.externalOrderNumber || '-'
                )}
              </div>
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
              >
                {t('orders:so.edit.customerFixed')}
              </Text>
            </Col>
            <Col span={6}>
              <Text style={formStyles.labelStyle}>{t('orders:so.fields.currency')}</Text>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={CURRENCY_OPTIONS.map((code) => ({ value: code, label: code }))}
                    size="large"
                    style={formStyles.inputStyle}
                  />
                )}
              />
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={6}>
              {renderDateField('expectedShipDate', t('orders:so.fields.expectedShipDate'))}
            </Col>
            <Col span={6}>{renderDateField('deliveryDate', t('orders:so.fields.deliveryDate'))}</Col>
          </Row>
        </div>
      </div>

      {/* 訂單明細 Lines — replace semantics: saved lines get NEW snapshots */}
      <div style={sectionCardStyle}>
        <div style={sectionHeaderStyle}>
          <Text strong>{t('orders:so.create.linesTitle')}</Text>
        </div>
        <div style={{ padding: '24px' }}>
          <Alert
            type="info"
            showIcon
            message={t('orders:so.edit.repriceNote')}
            style={{ marginBottom: '16px' }}
          />
          {linesError && (
            <Alert type="error" showIcon message={linesError} style={{ marginBottom: '16px' }} />
          )}
          <SoLineEditor
            lines={lines}
            products={products}
            productsLoading={productsLoading || isLoading}
            customerId={so?.customerId ?? null}
            currency={currency}
            onAddLine={() => setLines((prev) => [...prev, newDraftSoLine()])}
            onRemoveLine={(key) => setLines((prev) => prev.filter((line) => line.key !== key))}
            onSelectProduct={handleSelectProduct}
            onQtyChange={(key, qty) =>
              setLines((prev) => prev.map((line) => (line.key === key ? { ...line, qty } : line)))
            }
            onReResolveAtQty={handleReResolveAtQty}
            minimumMarginPct={minimumMarginPct}
            onApplyOverride={(key, unitPrice, reason) =>
              setLines((prev) =>
                prev.map((line) =>
                  line.key === key
                    ? { ...line, unitPrice, overridden: true, overrideReason: reason }
                    : line
                )
              )
            }
            onRestorePrice={(key) =>
              setLines((prev) =>
                prev.map((line) =>
                  line.key === key
                    ? {
                        ...line,
                        unitPrice: line.resolution?.unitPrice ?? null,
                        overridden: false,
                        overrideReason: null,
                      }
                    : line
                )
              )
            }
          />

          {/* Header totals — 合計 + 預估毛利 (Board D profit) */}
          <div
            className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-6"
            style={{
              padding: '16px 24px',
              backgroundColor: token.colorFillQuaternary,
              borderRadius: `${radiusTokens.card}px`,
            }}
          >
            <div className="tw-flex tw-items-baseline tw-gap-2">
              <Text type="secondary">{t('orders:so.create.totalAmount')}</Text>
              <Text
                strong
                style={{ fontSize: token.fontSizeHeading4, fontVariantNumeric: 'tabular-nums' }}
              >
                {formatMoney(currency, totals.amount)}
              </Text>
            </div>
            <div className="tw-flex tw-items-baseline tw-gap-2">
              <Text type="secondary">{t('orders:so.create.totalMargin')}</Text>
              {totals.margin !== null ? (
                <Text
                  strong
                  style={{
                    fontSize: token.fontSizeHeading4,
                    fontVariantNumeric: 'tabular-nums',
                    color: totals.margin >= 0 ? token.colorSuccess : token.colorError,
                  }}
                >
                  {formatMoney(currency, totals.margin)}
                  {totals.marginPct !== null ? ` (${totals.marginPct}%)` : ''}
                </Text>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </div>
          </div>

          {totals.pendingApproval && (
            <Alert
              type="warning"
              showIcon
              // The EDIT page speaks about the save it is about to make, and the
              // floor it quotes is TODAY's company minimum — the saved order's own
              // banner quotes the snapshot it was priced against (2026-09-16).
              message={t('orders:so.edit.pendingApprovalTitle')}
              description={
                // WHY first, then what is held — the same order as the saved
                // order's banner (SoHeader), so the two never read differently.
                <div className="tw-flex tw-flex-col tw-gap-1">
                  <ul style={{ margin: 0, paddingInlineStart: token.paddingLG }}>
                    {totals.holdReasons.map((reason) => (
                      <li key={reason}>
                        {t(holdReasonKey(reason, false), {
                          pct: SO_PRICE_DEVIATION_THRESHOLD_PCT,
                          floor: formatPct(minimumMarginPct),
                        })}
                      </li>
                    ))}
                  </ul>
                  <span>{t('orders:so.create.pendingApprovalConsequence')}</span>
                </div>
              }
              style={{ marginTop: '16px' }}
            />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="tw-flex tw-justify-end tw-gap-3">
        <Button
          size="large"
          onClick={() => navigate(soId ? `/auth/sales-orders/${soId}` : '/auth/sales-orders')}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.card,
          }}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          type="primary"
          size="large"
          loading={isSubmitting}
          disabled={isLoading || !so}
          onClick={handleSubmit(submit)}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.card,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {t('orders:so.edit.save')}
        </Button>
      </div>
    </div>
  );
};

export default SalesOrderEditPage;
